/**
 * Copy recipe rows from one portion scope to another, scaled by a factor —
 * "Half is 0.5 × Full" without retyping every ingredient.
 */

export interface ScalableMapping {
  id: number;
  inventoryItemId: number;
  inventoryItemName: string;
  unit: string;
  portionId?: number | null;
  portionName?: string | null;
  quantityRequired: number;
  batchQuantity?: number | null;
  batchYield?: number | null;
}

const round = (value: number) => Math.round(value * 10000) / 10000;

/**
 * Returns new draft mappings for the target scope: source rows scaled by
 * `factor`, skipping ingredients already present in the target scope.
 * Batch provenance doesn't survive scaling (the batch numbers would lie),
 * so copies carry only the per-portion quantity.
 */
export function scaleRecipeMappings<T extends ScalableMapping>(
  sourceMappings: T[],
  factor: number,
  targetPortionId: number | null,
  targetPortionName: string | null,
  existingTargetMappings: T[],
): ScalableMapping[] {
  if (!(factor > 0)) return [];
  const existing = new Set(existingTargetMappings.map((m) => m.inventoryItemId));
  return sourceMappings
    .filter((m) => !existing.has(m.inventoryItemId))
    .map((m) => ({
      id: 0, // draft — persisted on Save Recipe
      inventoryItemId: m.inventoryItemId,
      inventoryItemName: m.inventoryItemName,
      unit: m.unit,
      portionId: targetPortionId,
      portionName: targetPortionName,
      quantityRequired: round(m.quantityRequired * factor),
    }));
}
